import {dequal} from 'dequal/lite'
import {atom, useAtomValue} from 'jotai'
import {useAtomCallback} from 'jotai/utils'
import {useCallback, useState} from 'react'
import {SWRResponse, mutate} from 'swr'

import {API} from 'lib/constants'
import useAuthSWR from 'lib/hooks/use-auth-swr'
import useUser from 'lib/hooks/use-user'
import LogRocket from 'lib/logrocket'
import authFetch from 'lib/utils/auth-fetch'
import {ResponseError} from 'lib/utils/safe-fetch'

const MAX_REFRESH_INTERVAL_MS = 30_000
const FAST_REFRESH_INTERVAL_MS = MAX_REFRESH_INTERVAL_MS / 64
const INITIAL_REFRESH_INTERVAL_MS = MAX_REFRESH_INTERVAL_MS / 8

// List of all tasks retrieved from the server.
const tasksAtom = atom<CL.Task[]>([])

/**
 * A list of tasks that are active and have not been hidden.
 */
export function useActiveTasks() {
  return useAtomValue(tasksAtom)
}

/**
 * Use the global SWR mutate function to force a refresh of the activity data.
 */
function mutateActivity(user: CL.User) {
  return mutate<CL.Activity>([API.Activity, user])
}

/**
 * Hook to refresh activity.
 */
export function useRefreshActivity() {
  const {user} = useUser()
  return useCallback(() => mutateActivity(user), [user])
}

/**
 * Remove a task from the server and manually refresh the task list.
 */
export function useRemoveTask() {
  const {user} = useUser()
  return useCallback(
    async (taskId) => {
      // Remove task via the API.
      await authFetch(`${API.Activity}/${taskId}`, user, {
        options: {method: 'DELETE'}
      })
      // Force a refresh of the full activity list.
      await mutateActivity(user)
    },
    [user]
  )
}

/**
 * Hook for initiating the activity fetching on an interval and syncing to atoms.
 *
 * Note: this should only be used once in the application. Use the hooks above to retrieve, hide,
 * or manually refresh the data.
 */
export function useActivitySync(): SWRResponse<CL.Activity, ResponseError> {
  const [refreshInterval, setRefreshInterval] = useState(
    INITIAL_REFRESH_INTERVAL_MS
  )

  const onSuccess = useAtomCallback<void, [CL.Activity]>(
    useCallback((get, set, activity) => {
      const previousTasks = get(tasksAtom)
      if (!dequal(previousTasks, activity.taskProgress)) {
        // New data, set the new data and reset the refresh interval.
        set(tasksAtom, activity.taskProgress)
        setRefreshInterval(FAST_REFRESH_INTERVAL_MS)
      } else {
        // Slow down the refresh interval if the data is the same
        setRefreshInterval((refreshInterval) => {
          return refreshInterval < MAX_REFRESH_INTERVAL_MS
            ? refreshInterval * 2
            : refreshInterval
        })
      }
    }, [])
  )

  // Request activity on a specific interval
  return useAuthSWR<CL.Activity, ResponseError>(API.Activity, {
    onError(response, url) {
      LogRocket.captureException(response.error, {
        extras: {
          fetchUrl: url
        },
        tags: {
          type: 'activity'
        }
      })
      // Stop refreshing.
      setRefreshInterval(0)
    },
    onSuccess,
    refreshInterval,
    refreshWhenOffline: true, // For status bar updates.
    revalidateOnFocus: false // Do not trigger a refresh when the tab is refocused.
  })
}
